import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Query,
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useSuspenseInfiniteQuery,
  useSuspenseQueries,
  useSuspenseQuery,
} from '@tanstack/react-query'
import { centroid } from '@turf/centroid'
import { AllGeoJSON } from '@turf/helpers'
import { Feature, FeatureCollection, MultiPolygon, Polygon } from 'geojson'

import { MuiOption } from '@/components/curb/constants'
import { AppendShapesToLayer } from '@/modules/api/openapi/models/AppendShapesToLayer'
import { CreateLayerBody } from '@/modules/api/openapi/models/CreateLayerBody'
import { LayerResponse } from '@/modules/api/openapi/models/LayerResponse'
import { ListShapesPayload } from '@/modules/api/openapi/models/ListShapesPayload'
import { ListShapesResponseModel } from '@/modules/api/openapi/models/ListShapesResponseModel'
import { ListShapesResultModel } from '@/modules/api/openapi/models/ListShapesResultModel'
import { PatchShape } from '@/modules/api/openapi/models/PatchShape'
import { PostShape } from '@/modules/api/openapi/models/PostShape'
import { ShapesResultModel } from '@/modules/api/openapi/models/ShapesResultModel'
import { UpdateLayerBody } from '@/modules/api/openapi/models/UpdateLayerBody'
import { queryClientInstance, removeQueryCache } from '@/modules/api/queryClient'
import { apiV2, internalApi } from '@/modules/api/request'
import { OpenApiQueryParamsOmitRegionId } from '@/modules/api/types'
import { getNextPageParam } from '@/modules/api/util'
import type { LayerOption, Level } from '@/modules/layers/constants'
import { useCurrentProduct, useCurrentRegion } from '@/modules/urlRouting/hooks'
import { SEGMENTS } from '@/modules/urlRouting/paths'

const shapesForLayerQueryOptions = (queryParams: ListShapesPayload) => ({
  queryKey: [`/shapes/list`, queryParams],
  queryFn: async () => {
    const res: ListShapesResponseModel = await internalApi.shapes.listShapesDeprecated({
      requestBody: queryParams,
    })
    return res.data
  },
})

export const useShapesForLayer = (
  shapeLayerId?: string,
  boundaryShapeLayerUuid?: string,
  boundaryShapeUuids?: string[]
) => {
  const {
    data: { regionId },
  } = useCurrentRegion()

  return useQuery({
    ...shapesForLayerQueryOptions({
      region_id: regionId,
      shape_layer_uuid: shapeLayerId!,
      boundary_shape_layer_uuid: boundaryShapeLayerUuid,
      boundary_shape_uuids: boundaryShapeUuids,
    }),
    enabled: !!shapeLayerId,
  })
}

export type LayerToShapesDict = { [key: string]: ListShapesResultModel[] }

/**
 * Hook to get the shapes for one or more layers given an array of shape layer ids. Uses the same
 * react query keys as {@link useShapesForLayer} so that each layer that is fetched will be
 * individually cached.
 *
 * @param shapeLayerIds a list of layers to load shapes for
 * @returns a list of api responses' data from calling the /shapes/list endpoint multiple times
 */
export const useShapesForLayers = (
  shapeLayerIds: string[],
  boundaryShapeLayerUuid?: string,
  boundaryShapeUuids?: string[]
) => {
  const {
    data: { regionId },
  } = useCurrentRegion()
  const productPath = useCurrentProduct()

  const results = useQueries({
    queries: shapeLayerIds.map(shapeLayerId => {
      const externalQueryParams = {
        queryKey: [`/v2/regions/${regionId}/shapes/list`, { regionId, shapeLayerId }],
        queryFn: async () => {
          const res = await apiV2.shapes.listShapesDeprecated({
            regionId,
            requestBody: { region_id: regionId, shape_layer_uuid: shapeLayerId },
          })
          return res.data
        },
      }

      const internalQueryParams = shapesForLayerQueryOptions({
        region_id: regionId,
        shape_layer_uuid: shapeLayerId,
        boundary_shape_layer_uuid: boundaryShapeLayerUuid,
        boundary_shape_uuids: boundaryShapeUuids,
      })
      return productPath === SEGMENTS.PUBLIC ? externalQueryParams : internalQueryParams
    }),
  })
  const isLoading = results.some(result => result.isLoading)
  const isError = results.some(result => result.isError)
  const error = results.find(result => result.isError && result.error)
  // Memoizing b/c useQueries returns a new array on every re-render.
  // Using a simple array of the dataUpdatedAt timestamps so that the result gets refreshed whenever the data changes.
  // See: https://github.com/TanStack/query/issues/3049#issuecomment-1253201068
  const layerToShapesDict = useMemo(() => {
    return isLoading
      ? undefined
      : results.reduce<LayerToShapesDict>((layerToShapesDict, result, index) => {
          layerToShapesDict[shapeLayerIds[index]] = result.data!
          return layerToShapesDict
        }, {})
  }, [JSON.stringify(results.map(result => result.dataUpdatedAt))])

  return { isLoading, isError, error, layerToShapesDict }
}

/**
 * Hook to get the Shape layers available to a region
 * @param regionId
 * @returns
 */
export const useLayers = (
  queryParams: OpenApiQueryParamsOmitRegionId<typeof internalApi.layers.getLayers> = {},
  enabled = true
) => {
  const {
    data: { regionId },
  } = useCurrentRegion()

  return useQuery({
    queryKey: [`/regions/${regionId}/layers`, queryParams],
    queryFn: async () => {
      const layersResponse = await internalApi.layers.getLayers({
        regionId,
        ...queryParams,
      })

      layersResponse.items.forEach(layer =>
        queryClientInstance.setQueryData<LayerResponse>(
          [`/regions/${regionId}/layers/${layer.shapeLayerUuid}`],
          layer
        )
      )

      // Tripwire: a single page of layers silently dropping rows is exactly how the policy
      // Geography column regressed to "Citywide". Shout in dev so the next accidental truncation
      // is caught at the source rather than as a mystery display bug.
      if (import.meta.env.DEV && !import.meta.env.VITEST) {
        const { items, total } = layersResponse
        // A caller that asked for a specific page meant to paginate — don't cry truncation at it.
        const isExplicitPageRead = queryParams.page != null
        if (!isExplicitPageRead && total != null && items.length < total) {
          // eslint-disable-next-line no-console
          console.warn(
            `useLayers: got ${items.length}/${total} layers for region "${regionId}" — the ` +
              'result is one truncated page. Use useAllLayers() or pass a larger `size` if you ' +
              'need the full set.'
          )
        }
      }

      return layersResponse
    },
    placeholderData: {
      items: [] as LayerResponse[],
      total: 0,
      page: 1,
      size: 0,
      pages: 0,
    },
    enabled,
  })
}

/**
 * Fetch *every* layer for the region by paging through the full result set, instead of the
 * single (default 50-row) page {@link useLayers} returns. Restores the legacy unpaginated
 * `layerStore` semantics and replaces the scattered `useLayers({ size: 100 })` workaround. Use
 * this whenever you need the complete layer set — UUID→name lookups, dropdown options — rather
 * than a single page; `useLayers` remains for genuinely paginated/filtered/single-page reads.
 */
export const useAllLayers = (
  queryParams: OpenApiQueryParamsOmitRegionId<typeof internalApi.layers.getLayers> = {}
) => {
  const {
    data: { regionId },
  } = useCurrentRegion()

  return useQuery({
    queryKey: [`/regions/${regionId}/layers`, { ...queryParams, all: true }],
    queryFn: async () => {
      // `size: 100` is the API max per page (fastapi_pagination `le=100`); looping every page
      // removes the >100 ceiling that a bare `size: 100` would still hit.
      const firstPage = await internalApi.layers.getLayers({
        regionId,
        ...queryParams,
        size: 100,
        page: 1,
      })
      const pageCount = firstPage.pages ?? 1
      const remainingPages =
        pageCount > 1
          ? await Promise.all(
              Array.from({ length: pageCount - 1 }, (_, i) =>
                internalApi.layers.getLayers({ regionId, ...queryParams, size: 100, page: i + 2 })
              )
            )
          : []
      const items = [firstPage, ...remainingPages].flatMap(page => page.items)

      items.forEach(layer =>
        queryClientInstance.setQueryData<LayerResponse>(
          [`/regions/${regionId}/layers/${layer.shapeLayerUuid}`],
          layer
        )
      )

      return {
        items,
        total: firstPage.total ?? items.length,
        page: 1,
        size: items.length,
        pages: 1,
      }
    },
    placeholderData: {
      items: [] as LayerResponse[],
      total: 0,
      page: 1,
      size: 0,
      pages: 0,
    },
  })
}

/**
 * Hook to get the Shape layers available to a region
 * @param regionId
 * @returns
 */
export const useInfiniteLayers = (
  queryParams: OpenApiQueryParamsOmitRegionId<typeof internalApi.layers.getLayers> = {}
) => {
  const {
    data: { regionId },
  } = useCurrentRegion()

  return useInfiniteQuery({
    queryKey: [`/regions/${regionId}/layers`, queryParams],
    queryFn: async ({ pageParam }) => {
      const layersResponse = await internalApi.layers.getLayers({
        regionId,
        page: pageParam,
        ...queryParams,
      })

      layersResponse.items.forEach(layer =>
        queryClientInstance.setQueryData<LayerResponse>(
          [`/regions/${regionId}/layers/${layer.shapeLayerUuid}`],
          layer
        )
      )

      return layersResponse
    },
    initialPageParam: 1,
    getNextPageParam,
  })
}

/**
 * Hook to get the Shape layers available to a region, with Suspense
 * @param regionId
 * @returns
 */
export const useSuspenseLayers = (
  queryParams: OpenApiQueryParamsOmitRegionId<typeof internalApi.layers.getLayers> = {}
) => {
  const {
    data: { regionId },
  } = useCurrentRegion()

  return useSuspenseQuery({
    queryKey: [`/regions/${regionId}/layers`, queryParams],
    queryFn: async () => {
      const layersResponse = await internalApi.layers.getLayers({
        regionId,
        ...queryParams,
      })

      layersResponse.items.forEach(layer =>
        queryClientInstance.setQueryData<LayerResponse>(
          [`/regions/${regionId}/layers/${layer.shapeLayerUuid}`],
          layer
        )
      )

      return layersResponse
    },
  })
}

/**
 * Get layers for region formatted for Dropdown values
 * @param searchParams
 * @returns
 */
export const useLayerOptions = (
  queryParams: OpenApiQueryParamsOmitRegionId<typeof internalApi.layers.getLayers> = {}
) => {
  const { t } = useTranslation()

  const { data } = useAllLayers(queryParams)
  const { items: layers } = data || { items: [] }

  return layers.map(layer => ({
    text: `${
      (layer.level as unknown as string) === 'l1'
        ? `${t('routesMap.primaryLayer', 'Primary Layer')}: `
        : ''
    }${layer.layerName}`,
    value: layer.shapeLayerUuid,
    key: layer.shapeLayerUuid,
    level: layer.level,
    'data-testid': layer.layerName.toLowerCase().replaceAll(' ', '-'),
  }))
}

export const useLayerOptionsMui = (
  queryParams: OpenApiQueryParamsOmitRegionId<typeof internalApi.layers.getLayers> = {}
): MuiOption[] => {
  const { t } = useTranslation()

  const { data } = useAllLayers(queryParams)
  const { items: layers } = data || { items: [] }

  return layers.map(layer => ({
    label: `${
      (layer.level as unknown as string) === 'l1'
        ? `${t('routesMap.primaryLayer', 'Primary Layer')}: `
        : ''
    }${layer.layerName}`,
    value: layer.shapeLayerUuid,
  }))
}

/**
 * Get the layer to use as a default when the user/feature has not specified a layer to use.
 */
export const useDefaultLayerOption = () => {
  const layerOptions = useLayerOptions()
  return layerOptions?.[0]
}

/**
 * UUID of the region's `region_boundary` layer, or `''` while loading / if the region has none.
 *
 * This is the right default for features that aggregate over the whole region: the curb dashboard
 * and citation date-range endpoints all key off `region_boundary` server-side. Unlike the parking
 * insights `useDefaultLayer`, it does not require the region to have *parking* revenue data, so it
 * still resolves for regions whose only curb data is citations.
 */
export const useRegionBoundaryLayerUuid = (): string => {
  const { data } = useAllLayers()
  const items = data?.items ?? []

  return (
    items.find(layer => layer.level === 'region_boundary')?.shapeLayerUuid ??
    items[0]?.shapeLayerUuid ??
    ''
  )
}

// Levels that are not part of the district hierarchy and are excluded from the
// layer dropdown options (mirrors `layerStore.availableLayerOptions`).
const NON_DISTRICT_LEVELS = ['blockface', 'cfad', 'parking_districts']

/**
 * Filter out the non-district levels, sort by level then name, and format as
 * `LayerOption`s. Shared by `useAvailableLayerOptions` and
 * `useVehicleCountLayerOptions`.
 */
const toDistrictLayerOptions = (
  layers: LayerResponse[],
  t: ReturnType<typeof useTranslation>['t']
): LayerOption[] =>
  layers
    .filter(l => !NON_DISTRICT_LEVELS.includes((l.level as string) ?? ''))
    .sort((a, b) => {
      const aLevel = a.level as string | null
      const bLevel = b.level as string | null
      // Match the legacy `_.sortBy([level, layerName])` ordering, which ranks
      // null/undefined levels last rather than first (as `?? ''` would).
      if (aLevel !== bLevel) {
        if (aLevel == null) return 1
        if (bLevel == null) return -1
        return aLevel.localeCompare(bLevel)
      }
      return a.layerName.localeCompare(b.layerName)
    })
    .map(l => ({
      text:
        (l.level as unknown as string) === 'l1'
          ? `${t('routesMap.primaryLayer', 'Primary Layer')}: ${l.layerName}`
          : l.layerName,
      value: l.shapeLayerUuid,
      level: l.level as Level,
    }))

/**
 * District-level layer dropdown options (excludes the non-district levels), sorted
 * by level then name. Replaces `layerStore.availableLayerOptions`.
 *
 * Uses `useAllLayers()` so the dropdown lists every layer, matching the legacy store's
 * unpaginated `/layers/list` load (a single page would silently truncate the options).
 */
export const useAvailableLayerOptions = (): LayerOption[] => {
  const { t } = useTranslation()

  const { data } = useAllLayers()

  return toDistrictLayerOptions(data?.items ?? [], t)
}

/**
 * Layer dropdown options limited to layers that have vehicle counts, excluding the
 * non-district levels. Replaces `layerStore.availableVehicleCountOptions`.
 *
 * Filters server-side to vehicle-count layers and loads every page (see
 * `useAvailableLayerOptions` for the pagination note).
 */
export const useVehicleCountLayerOptions = (): LayerOption[] => {
  const { t } = useTranslation()

  const { data } = useAllLayers({ vehicleCounts: true })

  return toDistrictLayerOptions(
    (data?.items ?? []).filter(l => l.vehicleCounts),
    t
  )
}

/**
 * Hook to get a Shape layer
 * @param regionId
 * @returns
 */
export const useLayer = (shapeLayerUuid?: string | undefined) => {
  const {
    data: { regionId },
  } = useCurrentRegion()

  return useQuery({
    queryKey: [`/regions/${regionId}/layers/${shapeLayerUuid}`],
    queryFn: async () =>
      await internalApi.layers.getLayer({ regionId, shapeLayerUuid: shapeLayerUuid! }),
    enabled: !!shapeLayerUuid,
  })
}

/**
 * Name of the layer an applied-filter chip refers to.
 *
 * Deliberately a single-layer read rather than a lookup in whatever list the page already holds.
 * The chip has to name *whatever* `?shape_layer=` points at — including a layer the filter
 * drawer's picker does not offer, and one past the first page of a paginated list — and it has to
 * do it without a gap: resolving it from `useAllLayers` meant the chip vanished while that query
 * served its empty placeholder, leaving the filter counted but with nothing to read or remove.
 *
 * Costs nothing extra in the common case: `useAllLayers` seeds this exact query key per layer, so
 * once the list has loaded this is a cache hit, and before that it fetches just the one layer.
 */
export const useAppliedLayerName = (shapeLayerUuid?: string): string | undefined =>
  useLayer(shapeLayerUuid).data?.layerName

/**
 * False while `useAllLayers` serves its empty placeholder. The mobility maps take their default
 * layer from that list, so until it loads a header cannot tell if the URL's layer is the default
 * or a filter. Without this check, the default layer shows as a filter chip for a moment.
 */
export const useAllLayersLoaded = (): boolean => !useAllLayers().isPlaceholderData

/**
 * True when the layer list has loaded and is empty: the region has no layers. False while the
 * list loads or if its request fails, so the "no layers" message does not show by mistake.
 */
export const useRegionHasNoLayers = (): boolean => {
  const { data, isSuccess, isPlaceholderData } = useAllLayers()
  return isSuccess && !isPlaceholderData && data.items.length === 0
}

/**
 * Hook to get the first Shape layer at the given level (e.g. 'l1').
 * `data` is undefined while the layer list is loading or when the region has
 * no layer at that level. Replaces `layerStore.getLayerForLevel` /
 * `getLayerUUIDForLevel`.
 *
 * Loads every layer so the target level isn't missed when it falls outside the
 * first page (see `useAvailableLayerOptions`).
 */
export const useLayerForLevel = (level: string) => {
  const query = useAllLayers()
  const layer = query.data?.items.find(l => (l.level as unknown as string) === level)
  return { ...query, data: layer }
}

export interface Districts {
  geojson: FeatureCollection<Polygon | MultiPolygon>
  districtIdToName: Record<string, string>
  districtUUIDToName: Record<string, string>
}

const selectDistricts = (shapes: ListShapesResultModel[]): Districts => ({
  geojson: {
    type: 'FeatureCollection',
    features: shapes.map((shape): Feature<Polygon | MultiPolygon> => ({
      type: 'Feature',
      geometry: shape.geom as Polygon | MultiPolygon,
      id: shape.shape_uuid,
      properties: {
        name: shape.shape_name ?? shape.shape_id,
        description: shape.shape_description,
        created_time: shape.created_time,
        modified_time: shape.modified_time,
        color: '#5281ff',
        id: shape.shape_uuid,
        shape_uuid: shape.shape_uuid,
        shape_id: shape.shape_id,
      },
    })),
  },
  districtIdToName: Object.fromEntries(
    shapes.map(shape => [shape.shape_id, shape.shape_name ?? shape.shape_id])
  ),
  districtUUIDToName: Object.fromEntries(
    shapes.map(shape => [shape.shape_uuid, shape.shape_name ?? shape.shape_uuid])
  ),
})

/**
 * Hook to get the region's l1 ("districts") layer shapes as geojson, plus
 * shape_id → name and shape_uuid → name lookup maps. Replaces
 * `layerStore.districts` / `districtIdToName` / `districtUUIDToName`.
 * Loaded once per region + layer (`staleTime: Infinity`).
 */
export const useDistricts = () => {
  const {
    data: { regionId },
  } = useCurrentRegion()
  const { data: l1Layer } = useLayerForLevel('l1')

  return useQuery({
    ...shapesForLayerQueryOptions({
      region_id: regionId,
      shape_layer_uuid: l1Layer?.shapeLayerUuid as string,
    }),
    enabled: !!l1Layer?.shapeLayerUuid,
    staleTime: Infinity,
    select: selectDistricts,
  })
}

export const useLayersModifiedDate = (shapeLayerIds: string[]) => {
  const {
    data: { regionId },
  } = useCurrentRegion()
  const results = useSuspenseQueries({
    queries: shapeLayerIds.map(shapeLayerUuid => ({
      queryKey: [`/regions/${regionId}/layers/${shapeLayerUuid}`],
      queryFn: async () => await internalApi.layers.getLayer({ regionId, shapeLayerUuid }),
    })),
  })

  return results.reduce((previousDate, result) => {
    const modifiedTime = Date.parse(result.data.modifiedTime)
    return previousDate > modifiedTime ? previousDate : modifiedTime
  }, Date.parse('2000-01-01'))
}

export const useSuspenseLayer = (shapeLayerUuid?: string | undefined) => {
  const {
    data: { regionId },
  } = useCurrentRegion()

  return useSuspenseQuery({
    queryKey: [`/regions/${regionId}/layers/${shapeLayerUuid}`],
    queryFn: async () =>
      await internalApi.layers.getLayer({
        regionId,
        shapeLayerUuid: shapeLayerUuid!,
      }),
  })
}

export const useUpdateLayer = (shapeLayerUuid: string) => {
  const {
    data: { regionId },
  } = useCurrentRegion()

  return useMutation({
    mutationFn: async (requestBody: UpdateLayerBody) =>
      await internalApi.layers.updateLayer({ regionId, shapeLayerUuid, requestBody }),
    onSuccess: async data => {
      await removeQueryCache({
        predicate: (query: Query) => (query.queryKey[0] as string).includes('/layers'),
      })
      await queryClientInstance.setQueryData(
        [`/regions/${regionId}/layers/${data.shapeLayerUuid}`],
        data
      )
    },
  })
}

export const useCreateLayer = () => {
  const {
    data: { regionId },
  } = useCurrentRegion()

  return useMutation({
    mutationFn: async (requestBody: CreateLayerBody) =>
      await internalApi.layers.createLayer({ regionId, requestBody }),
    onSuccess: async data => {
      await queryClientInstance.setQueryData(
        [`/regions/${regionId}/layers/${data.shapeLayerUuid}`],
        data
      )
      await removeQueryCache({
        predicate: (query: Query) => (query.queryKey[0] as string).includes('/layers'),
      })
    },
  })
}

export const useLayerDelete = (shapeLayerUuid: string) => {
  const {
    data: { regionId },
  } = useCurrentRegion()

  return useMutation({
    mutationFn: async () => await internalApi.layers.deleteLayer({ regionId, shapeLayerUuid }),
    onSuccess: async () => {
      await removeQueryCache({ queryKey: [`/regions/${regionId}/layers/${shapeLayerUuid}`] })
      await removeQueryCache({
        predicate: (query: Query) => (query.queryKey[0] as string).includes('/layers'),
      })
    },
  })
}

export const useShapesByLayer = (
  shapeLayerUuid: string,
  queryParams: Partial<typeof internalApi.shapes.getShapesByLayer> = {}
) => {
  const {
    data: { regionId },
  } = useCurrentRegion()

  return useSuspenseInfiniteQuery({
    queryKey: [`/regions/${regionId}/layers/${shapeLayerUuid}/shapes`, queryParams],
    queryFn: async ({ pageParam }) => {
      const shapesResponse = await internalApi.shapes.getShapesByLayer({
        regionId,
        shapeLayerUuid,
        page: pageParam,
        ...queryParams,
      })

      shapesResponse.items.forEach(shape =>
        queryClientInstance.setQueryData<ShapesResultModel>([`/shapes/${shape.shapeUuid}`], shape)
      )

      return shapesResponse
    },
    initialPageParam: 1,
    getNextPageParam,
  })
}

export const useInifiniteShapes = (
  shapeLayerUuid: string,
  queryParams: Partial<typeof internalApi.shapes.getShapesByLayer> = {}
) => {
  const {
    data: { regionId },
  } = useCurrentRegion()

  return useInfiniteQuery({
    queryKey: [`/regions/${regionId}/layers/${shapeLayerUuid}/shapes`, queryParams],
    queryFn: async ({ pageParam }) => {
      const shapesResponse = await internalApi.shapes.getShapesByLayer({
        regionId,
        shapeLayerUuid,
        page: pageParam,
        ...queryParams,
      })

      shapesResponse.items.forEach(shape =>
        queryClientInstance.setQueryData<ShapesResultModel>([`/shapes/${shape.shapeUuid}`], shape)
      )

      return shapesResponse
    },
    initialPageParam: 1,
    getNextPageParam,
  })
}

// Module level so `useQueries` can memoize the combined array. An inline function is a new
// reference on every render, which would rebuild every caller's tag list each time.
// An entry is `undefined` only while its fetch is still pending. A query that has settled
// with no data - a failed fetch, which is terminal because the client retries 0 times -
// gives `null`, so callers still show a chip for the shape and can remove its filter.
const combineShapeNames = (
  results: { data?: { shapeName?: string | null }; isPending: boolean }[]
) => results.map(result => (result.isPending ? undefined : (result.data?.shapeName ?? null)))

export const useShapeNames = (shapeUuids: (string | null)[]) => {
  const {
    data: { regionId },
  } = useCurrentRegion()
  const productPath = useCurrentProduct()

  return useQueries({
    queries: shapeUuids.map(id =>
      productPath === SEGMENTS.PUBLIC
        ? {
            queryKey: [`/v2/regions/${regionId}/shapes/${id}`],
            queryFn: async () => await apiV2.shapes.getShape({ regionId, shapeUuid: id! }),
            enabled: !!id,
          }
        : {
            queryKey: [`/shapes/${id}`],
            queryFn: async () => await internalApi.shapes.getShape({ shapeUuid: id! }),
            enabled: !!id,
          }
    ),
    combine: combineShapeNames,
  })
}

export const useShape = (id: string) => {
  const {
    data: { regionId },
  } = useCurrentRegion()
  const productPath = useCurrentProduct()

  const externalQueryParams = {
    queryKey: [`/v2/regions/${regionId}/shapes/${id}`],
    queryFn: async () => await apiV2.shapes.getShape({ regionId, shapeUuid: id }),
  }

  const internalQueryParams = {
    queryKey: [`/shapes/${id}`],
    queryFn: async () => await internalApi.shapes.getShape({ shapeUuid: id }),
  }

  return useSuspenseQuery(
    productPath === SEGMENTS.PUBLIC ? externalQueryParams : internalQueryParams
  )
}

export const useShapeHasPoliciesV2 = (shapeUuid: string) =>
  useSuspenseQuery({
    queryKey: [`/shapes/${shapeUuid}/has_policies_v2`],
    queryFn: async () => await internalApi.shapes.getDoesShapeHavePoliciesV2({ shapeUuid }),
  })

export const useLayerHasPolicies = (shapeLayerUuid: string) => {
  const {
    data: { regionId },
  } = useCurrentRegion()

  const { data } = useSuspenseQuery({
    queryKey: [`/regions/${regionId}/layers/${shapeLayerUuid}/has_policies`],
    queryFn: async () =>
      await internalApi.layers.getDoesLayerHavePolicies({ regionId, shapeLayerUuid }),
  })

  return data
}

export const useShapePatch = (shapeUuid: string) =>
  useMutation({
    mutationFn: async (requestBody: PatchShape) =>
      await internalApi.shapes.patchShape({ shapeUuid, requestBody }),
    onSuccess: async data => {
      await queryClientInstance.setQueryData([`/shapes/${shapeUuid}`], data)
      await removeQueryCache({
        predicate: (query: Query) => (query.queryKey[0] as string).includes(`/layers`),
      })
      await removeQueryCache({
        predicate: (query: Query) => (query.queryKey[0] as string).includes(`/shapes/list`),
      })
      await removeQueryCache({
        predicate: (query: Query) => (query.queryKey[0] as string).includes(`/curbs`),
      })
      await removeQueryCache({
        predicate: (query: Query) => (query.queryKey[0] as string).includes(`/policies_v2`),
      })
      await removeQueryCache({
        predicate: (query: Query) => (query.queryKey[0] as string).includes(`/rules`),
      })
    },
  })

export const useShapePost = () =>
  useMutation({
    mutationFn: async (requestBody: PostShape) =>
      await internalApi.shapes.postShape({ requestBody }),
    onSuccess: async data => {
      await queryClientInstance.setQueryData([`/shapes/${data.shapeUuid}`], data)
      await removeQueryCache({
        predicate: (query: Query) => (query.queryKey[0] as string).includes('/layers'),
      })
      await removeQueryCache({
        predicate: (query: Query) => (query.queryKey[0] as string).includes('/policies_v2'),
      })
      await removeQueryCache({
        predicate: (query: Query) => (query.queryKey[0] as string).includes('/layers'),
      })
      await removeQueryCache({
        predicate: (query: Query) => (query.queryKey[0] as string).includes(`/rules`),
      })
    },
  })

export const useShapeUpload = (shapeLayerUuid: string) => {
  const {
    data: { regionId },
  } = useCurrentRegion()

  return useMutation({
    mutationFn: async (requestBody: AppendShapesToLayer) =>
      await internalApi.shapes.uploadShapesToLayer({ regionId, shapeLayerUuid, requestBody }),
    onSuccess: async data => {
      await removeQueryCache({
        predicate: (query: Query) => (query.queryKey[0] as string).includes('/layers'),
      })
      await queryClientInstance.setQueryData(
        [`/regions/${regionId}/layers/${data.shapeLayerUuid}`],
        data
      )
    },
  })
}

export const useShapeDelete = (shapeUuid: string) => {
  const {
    data: { regionId },
  } = useCurrentRegion()

  const associatedKeys = ['layers', 'policies_v2', 'rules']

  return useMutation({
    mutationFn: async () => await internalApi.shapes.deleteShape({ shapeUuid }),
    onSuccess: async data => {
      await removeQueryCache({ queryKey: [`/shapes/${shapeUuid}`] })
      await removeQueryCache({
        predicate: (query: Query) =>
          associatedKeys.some(key => (query.queryKey[0] as string).includes(key)),
      })
      await queryClientInstance.setQueryData(
        [`/regions/${regionId}/layers/${data.shapeLayerUuid}`],
        data
      )
    },
  })
}

export const fetchShape = (id?: string | undefined) => {
  if (!id) return undefined

  // TODO: make the queryKey and queryFn reusable
  return queryClientInstance.fetchQuery({
    queryKey: [`/shape/${id}`],
    queryFn: async () =>
      await internalApi.shapes.getShape({
        shapeUuid: id!,
      }),
  })
}

export const fetchShapeCentroidLngLat = async (
  id?: string | undefined
): Promise<[number, number] | undefined> => {
  const shape = await fetchShape(id)
  if (!shape) return undefined
  return centroid(shape.geom as AllGeoJSON).geometry.coordinates as [number, number]
}
